import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AgentBuilder } from "@/components/AgentBuilder";
import { ProjectAnalytics } from "@/components/ProjectAnalytics";
import { ProjectExport } from "@/components/ProjectExport";
import { CollaborationPresence } from "@/components/CollaborationPresence";
import { WorkflowConfirmModal } from "@/components/project-tabs/WorkflowConfirmModal";
import { ProjectOverview } from "@/components/project-tabs/ProjectOverview";
import { ProjectBoard } from "@/components/project-tabs/ProjectBoard";
import { ProjectTasks } from "@/components/project-tabs/ProjectTasks";
import { ProjectAssets } from "@/components/project-tabs/ProjectAssets";
import { ProjectConversations } from "@/components/project-tabs/ProjectConversations";
import { ProjectPipelines } from "@/components/project-tabs/ProjectPipelines";
import { ProjectDependencies } from "@/components/project-tabs/ProjectDependencies";
import { ProjectTeam } from "@/components/project-tabs/ProjectTeam";
import { ProjectComments } from "@/components/project-tabs/ProjectComments";
import { API, STAGE_STATUS } from "@/components/project-tabs/constants";
import {
  ChevronLeft, CalendarDays, MessageSquare, Zap, FileText, Loader2,
  ListTodo, BarChart3, Columns, GitBranch, Users, TrendingUp, Download,
  ArrowRight,
} from "lucide-react";

export const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("stage");
  const [tab, setTab] = useState("overview");
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showAgentBuilder, setShowAgentBuilder] = useState(false);
  const [lifecycleUpdating, setLifecycleUpdating] = useState({});
  const [runningPipeline, setRunningPipeline] = useState(null);
  const [pipelineProgress, setPipelineProgress] = useState({});
  const [orchestrations, setOrchestrations] = useState([]);

  const fetchDetail = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/projects/${projectId}/detail`, { withCredentials: true });
      if (!d || typeof d !== 'object') return;
      setData(d);
      if (d.project && !d.project.workflow_confirmed && !d.project.seeded && (!d.project.workflow_stages || d.project.workflow_stages.length === 0)) {
        setShowWorkflowModal(true);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  /* SSE for live updates */
  useEffect(() => {
    let es;
    try {
      es = new EventSource(`${API}/projects/${projectId}/events`, { withCredentials: true });
      es.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (["task_updated", "comment_added", "team_updated"].includes(ev.type)) fetchDetail();
        } catch {}
      };
      es.onerror = () => es?.close();
    } catch {}
    return () => es?.close();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Fetch orchestrations */
  useEffect(() => {
    (async () => { try { const { data: o } = await axios.get(`${API}/orchestrations`, { withCredentials: true }); setOrchestrations(Array.isArray(o) ? o : []); } catch {} })();
  }, []);

  /* Handlers */
  const updateTaskStatus = async (taskId, status) => { try { await axios.put(`${API}/tasks/${taskId}`, { status }, { withCredentials: true }); fetchDetail(); } catch (e) { console.error(e); } };
  const updateTaskFields = async (taskId, fields) => { try { await axios.put(`${API}/tasks/${taskId}`, fields, { withCredentials: true }); fetchDetail(); } catch (e) { console.error(e); } };
  const updateLifecycle = async (artifactId, newStatus) => { setLifecycleUpdating(p => ({ ...p, [artifactId]: true })); try { await axios.put(`${API}/artifacts/${artifactId}/lifecycle`, { status: newStatus }, { withCredentials: true }); fetchDetail(); } catch (e) { console.error(e); } finally { setLifecycleUpdating(p => ({ ...p, [artifactId]: false })); } };
  const saveDependencies = async (taskId, deps) => { try { await axios.put(`${API}/tasks/${taskId}/dependencies`, { depends_on: deps }, { withCredentials: true }); fetchDetail(); } catch (e) { console.error(e); } };

  const onDragEnd = useCallback(async (result) => {
    const { draggableId, destination, source } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
    if (view === "stage") await updateTaskFields(draggableId, { stage: destination.droppableId });
    else await updateTaskFields(draggableId, { status: destination.droppableId });
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const runPipeline = async (orchId) => {
    if (runningPipeline) return;
    setRunningPipeline(orchId);
    setPipelineProgress({});
    try {
      const es = new EventSource(`${API}/orchestrations/${orchId}/run`, { withCredentials: true });
      es.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "agent_progress") setPipelineProgress(p => ({ ...p, [ev.data.index]: ev.data.progress }));
          else if (ev.type === "agent_completed") setPipelineProgress(p => ({ ...p, [ev.data.index]: 100, [`${ev.data.index}_done`]: true }));
          else if (ev.type === "run_completed" || ev.type === "done") { setRunningPipeline(null); es.close(); fetchDetail(); }
          else if (ev.type === "error") { setRunningPipeline(null); es.close(); }
        } catch {}
      };
      es.onerror = () => { setRunningPipeline(null); es.close(); };
    } catch { setRunningPipeline(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-foreground/45" /></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-foreground/50">Project not found</div>;

  const { project, tasks, comments, sessions, artifacts, resources, activities, metrics, milestones } = data;
  const stages = project.workflow_stages || [];
  const teamMembers = project.team_members || project.team || [];

  const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "board", label: "Board", icon: Columns },
    { id: "tasks", label: `Tasks (${tasks.length})`, icon: ListTodo },
    { id: "assets", label: `Assets (${artifacts.length + resources.length})`, icon: FileText },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "conversations", label: `Conversations (${sessions.length})`, icon: MessageSquare },
    { id: "pipelines", label: "Pipelines", icon: Zap },
    { id: "dependencies", label: "Dependencies", icon: GitBranch },
    { id: "export", label: "Export", icon: Download },
    { id: "team", label: "Team", icon: Users },
    { id: "comments", label: `Comments (${comments.length})`, icon: MessageSquare },
  ];

  return (
    <div className="flex flex-col h-full bg-[#f8f9fb]" data-testid="project-detail-page">
      {showWorkflowModal && <WorkflowConfirmModal projectId={projectId} onConfirmed={() => { setShowWorkflowModal(false); fetchDetail(); }} onClose={() => setShowWorkflowModal(false)} />}
      <AgentBuilder isOpen={showAgentBuilder} onClose={() => setShowAgentBuilder(false)} onCreated={() => fetchDetail()} />

      {/* ─── Header ─── */}
      <div className="px-6 pt-5 pb-4 bg-white border-b border-border/40 shrink-0">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate("/projects")} className="text-[11px] text-foreground/40 hover:text-foreground flex items-center gap-1" data-testid="back-to-projects"><ChevronLeft className="w-3.5 h-3.5" /> Projects</button>
          <div className="w-px h-3.5 bg-border/40" />
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${project.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : project.status === "completed" ? "bg-[#014d7d]/8 text-[#014d7d] border-[#014d7d]/20" : project.status === "blocked" ? "bg-red-50 text-red-600 border-red-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>{project.status?.replace("_", " ")}</span>
          {project.due_date && <span className="text-[11px] text-foreground/50 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Due {new Date(project.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
        </div>
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-bold text-xl text-foreground truncate">{project.name}</h1>
            {project.description && <p className="text-xs text-foreground/40 mt-0.5 max-w-xl truncate">{project.description}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <CollaborationPresence projectId={projectId} activeTab={tab} />
            <div className="flex -space-x-2">
              {teamMembers.slice(0, 5)?.map((m, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-[9px] font-bold" style={{ background: m.color }} title={m.name}>{m.initials}</div>
              ))}
            </div>
            {!project.workflow_confirmed && !project.seeded && (
              <button onClick={() => setShowWorkflowModal(true)} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-1.5" data-testid="setup-workflow-btn"><GitBranch className="w-3 h-3" /> Setup Workflow</button>
            )}
            <Link to={`/projects/${projectId}`} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="open-workspace"><MessageSquare className="w-3 h-3" /> Workspace</Link>
            <button onClick={() => setShowAgentBuilder(true)} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-[#014d7d]/20 text-[#014d7d] flex items-center gap-1.5 hover:bg-[#014d7d]/5" data-testid="open-agent-builder"><Zap className="w-3 h-3" /> Agent Builder</button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#014d7d] to-[#178a55]" style={{ width: `${metrics?.completion_pct || project.progress || 0}%` }} /></div>
          <span className="text-xs font-heading font-bold text-foreground tabular-nums">{metrics?.completion_pct || project.progress || 0}%</span>
        </div>
      </div>

      {/* ─── Pipeline ─── */}
      {stages.length > 0 && (
        <div className="px-6 py-3 bg-white border-b border-border/40 shrink-0">
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {stages?.map((s, i) => {
              const sc = STAGE_STATUS[s.status] || STAGE_STATUS.pending;
              return (
                <div key={i} className="flex items-center gap-1.5 shrink-0">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${sc.light} border ${sc.border}`} data-testid={`pipeline-stage-${s.name.toLowerCase()}`}>
                    <div className={`w-2 h-2 rounded-full ${sc.bg} shrink-0`} />
                    <span className={`text-[11px] font-semibold ${sc.text} whitespace-nowrap`}>{s.name}</span>
                    <span className="text-[10px] text-foreground/50 font-medium">{s.artifacts || 0}</span>
                  </div>
                  {i < stages.length - 1 && <ArrowRight className="w-3 h-3 text-foreground/15 shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Tabs ─── */}
      <div className="px-6 py-2 bg-white border-b border-border/40 flex items-center justify-between shrink-0">
        <div className="flex gap-0.5">
          {TABS?.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${tab === t.id ? "bg-[#014d7d]/[0.06] text-[#014d7d]" : "text-foreground/50 hover:text-foreground/60 hover:bg-foreground/[0.02]"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "board" && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setView("stage")} className={`text-[10px] font-semibold px-2.5 py-1 rounded-md ${view === "stage" ? "bg-white text-foreground shadow-sm" : "text-foreground/40"}`} data-testid="view-stage"><GitBranch className="w-3 h-3 inline mr-1" />Stage</button>
            <button onClick={() => setView("status")} className={`text-[10px] font-semibold px-2.5 py-1 rounded-md ${view === "status" ? "bg-white text-foreground shadow-sm" : "text-foreground/40"}`} data-testid="view-status"><Columns className="w-3 h-3 inline mr-1" />Status</button>
          </div>
        )}
      </div>

      {/* ─── Content ─── */}
      <div className="flex-1 overflow-y-auto">
        {tab === "overview" && <ProjectOverview projectId={projectId} tasks={tasks} metrics={metrics} stages={stages} milestones={milestones} activities={activities} sessions={sessions} setTab={setTab} />}
        {tab === "board" && <ProjectBoard tasks={tasks} stages={stages} view={view} onDragEnd={onDragEnd} onUpdateFields={updateTaskFields} teamMembers={teamMembers} />}
        {tab === "tasks" && <ProjectTasks projectId={projectId} tasks={tasks} stages={stages} teamMembers={teamMembers} onStatusChange={updateTaskStatus} onUpdateFields={updateTaskFields} onRefresh={fetchDetail} />}
        {tab === "assets" && <ProjectAssets artifacts={artifacts} resources={resources} onLifecycleUpdate={updateLifecycle} lifecycleUpdating={lifecycleUpdating} />}
        {tab === "analytics" && <ProjectAnalytics projectId={projectId} />}
        {tab === "conversations" && <ProjectConversations projectId={projectId} sessions={sessions} />}
        {tab === "pipelines" && <ProjectPipelines orchestrations={orchestrations} runningPipeline={runningPipeline} pipelineProgress={pipelineProgress} onRunPipeline={runPipeline} onOpenBuilder={() => setShowAgentBuilder(true)} />}
        {tab === "dependencies" && <ProjectDependencies tasks={tasks} dependencyEdges={data.dependency_edges} onSaveDeps={saveDependencies} />}
        {tab === "export" && <ProjectExport projectId={projectId} projectName={project.name} />}
        {tab === "team" && <ProjectTeam projectId={projectId} teamMembers={teamMembers} onRefresh={fetchDetail} />}
        {tab === "comments" && <ProjectComments projectId={projectId} comments={comments} onRefresh={fetchDetail} />}
      </div>
    </div>
  );
};
